import { useEffect, useState } from 'react'
import { ActivityIndicator, ScrollView, Text, View, useWindowDimensions } from 'react-native'
import { useTranslation } from 'react-i18next'
import { screens } from '@chillout/core'
import { Button, Stepper, TextField } from '@chillout/ui'
import type { CashierMemberProfile } from '../home/useMemberSearch'
import { MemberInfoHeader } from '../stampAwarding/MemberInfoHeader'
import { SelectField } from '../../shared/SelectField'
import { RewardBalanceCard } from '../../shared/RewardBalanceCard'
import { useMemberRewardData } from './useMemberRewardData'

interface RewardRestoreSheetProps {
  member: CashierMemberProfile
  onClose: () => void
  onRequestPin: (rewardItemId: string, rewardName: string, restoreCount: number, reason: string, note: string) => void
}

export function RewardRestoreSheet({ member, onClose, onRequestPin }: RewardRestoreSheetProps) {
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  const twoColumn = width >= screens.lg
  const { data: rewardItems, isLoading } = useMemberRewardData(member.id)

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [restoreCount, setRestoreCount] = useState(1)
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')

  useEffect(() => {
    if (rewardItems?.length === 1 && !selectedItemId) {
      setSelectedItemId(rewardItems[0].rewardItemId)
    }
  }, [rewardItems, selectedItemId])

  const reasonOptions = (
    ['customer_complaint', 'system_error', 'cashier_mistake', 'promotion', 'other'] as const
  ).map((key) => ({ value: key, label: t(`common.adjustmentReasons.${key}`) }))

  const selectedItem = rewardItems?.find((r) => r.rewardItemId === selectedItemId) ?? null

  const countLabel = restoreCount === 1
    ? t('rewardRestore.stepper.rewardSingle')
    : t('rewardRestore.stepper.rewards', { count: restoreCount })

  const canSubmit = selectedItemId !== null && reason !== ''

  const handleRestorePress = () => {
    if (!selectedItem) return
    onRequestPin(selectedItem.rewardItemId, selectedItem.name, restoreCount, reason, note)
  }

  const balanceSection = (
    <View style={{ gap: 12 }}>
      <Text className="text-sm font-semibold text-primary">
        {t('rewardRestore.rewardBalance.title')}
      </Text>

      {isLoading && <ActivityIndicator />}

      {rewardItems && rewardItems.length === 0 && (
        <Text className="text-base text-secondary">
          {t('rewardRestore.noRewardItems')}
        </Text>
      )}

      {rewardItems && rewardItems.map((item) => (
        <RewardBalanceCard
          key={item.rewardItemId}
          name={item.name}
          count={item.currentCount}
          delta={item.rewardItemId === selectedItemId ? restoreCount : 0}
          selected={item.rewardItemId === selectedItemId}
          onPress={() => { setSelectedItemId(item.rewardItemId); setRestoreCount(1) }}
          accessibilityLabel={t('rewardRestore.rewardBalance.cardAccessibilityLabel', {
            name: item.name,
            count: item.currentCount,
          })}
        />
      ))}

      {selectedItem && (
        <Stepper
          value={restoreCount}
          onChange={setRestoreCount}
          min={1}
          max={20}
          label={t('rewardRestore.stepper.label')}
          countLabel={countLabel}
          decrementAccessibilityLabel={t('rewardRestore.stepper.decrementAccessibilityLabel')}
          incrementAccessibilityLabel={t('rewardRestore.stepper.incrementAccessibilityLabel')}
          countAccessibilityLabel={t('rewardRestore.stepper.countAccessibilityLabel', { count: restoreCount })}
        />
      )}
    </View>
  )

  const formSection = (
    <View style={{ gap: 16 }}>
      <SelectField
        label={t('rewardRestore.reason.label')}
        value={reason}
        options={reasonOptions}
        placeholder={t('rewardRestore.reason.placeholder')}
        onValueChange={setReason}
      />

      <TextField
        label={t('rewardRestore.note')}
        placeholder={t('rewardRestore.notePlaceholder')}
        value={note}
        onChangeText={setNote}
        multiline
        maxLength={200}
      />

      <Text className="text-base text-secondary">
        {t('rewardRestore.disclaimer')}
      </Text>

      <View style={{ gap: 12 }}>
        <Button
          label={t('rewardRestore.restoreButton')}
          onPress={handleRestorePress}
          disabled={!canSubmit}
        />
        <Button
          label={t('common.cancel')}
          onPress={onClose}
          variant="ghost"
        />
      </View>
    </View>
  )

  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ paddingBottom: 40 }}
    >
      <MemberInfoHeader
        member={member}
        title={t('rewardRestore.title')}
      />

      <View className="px-6" style={{ gap: 24 }}>
        {twoColumn ? (
          <View style={{ flexDirection: 'row', gap: 32 }}>
            <View style={{ flex: 1 }}>
              {balanceSection}
            </View>
            <View style={{ flex: 1 }}>
              {formSection}
            </View>
          </View>
        ) : (
          <>
            {balanceSection}
            {formSection}
          </>
        )}
      </View>
    </ScrollView>
  )
}
